import React, { Ref } from 'react'
import { css, cx } from '@emotion/css'
import { FormTextProps, FormTextContainer } from '.'
import { theme } from '@contrast-security-inc/design-system-foundations'

/**
 * Props that aren't otherwise explained are simply passed as attributes
 * to the input element
 */
export interface TextareaProps {
  /**
   * Will be passed as the ref to the actual input element
   */
  readonly inputRef?: Ref<HTMLTextAreaElement>

  /**
   * Used to fit the textarea to it's container
   */
  readonly expand?: boolean

  /**
   * Allows enabling the resize functionality of the textarea
   */
  readonly resize?: 'none' | 'both' | 'vertical' | 'horizontal'

  /**
   * Allows for custom styling of the text area component.
   */
  readonly classNameTextArea?: string

  readonly rows?: number

  readonly autoFocus?: boolean

  readonly e2eTestId?: string

  readonly maxLength?: number
}

export const cssTextareaBorderColor = '#cbcbcb'

const textareaCss = css`
  overflow: auto; /* remove scroll bars when they are not necessary */
  outline: none;
  resize: none;
  padding: 8px 12px;
  border: 1px solid ${cssTextareaBorderColor};
  border-radius: 3px;
  &::placeholder {
    opacity: 0.3;
  }
`

const textareaReadonlyCss = css`
  color: ${theme.color.neutral60};
  background-color: ${theme.color.neutral20};
  border-color: ${theme.color.neutral40};
`

export function Textarea({
  expand = false,
  resize,
  errorMessage,
  inputRef,
  classNameTextArea,
  e2eTestId,
  ...props
}: FormTextProps & TextareaProps) {
  return (
    <FormTextContainer expand={expand} errorMessage={errorMessage} {...props}>
      {({
        renderLabel,
        id,
        label,
        disabled,
        placeholder,
        onChange,
        value,
        readOnly,
        renderHelpContent: _renderHelpContent,
        edited: _edited,
        classNameFormTextPrimaryContainer: _classNameFormTextPrimaryContainer, // do not render this prop here
        classNameFormTextSecondaryContainer:
          _classNameFormTextSecondaryContainer, // do not render this prop here
        errorMessage, // do not render this prop here
        ...props
      }) => (
        <textarea
          {...props}
          ref={inputRef}
          // provide aria-label when we can't ensure a label is associated
          // with the control
          aria-label={renderLabel || !id ? label : undefined}
          aria-labelledby={props['aria-labelledby']}
          data-testid={props['data-testid']}
          data-e2e={e2eTestId}
          disabled={disabled}
          id={id}
          placeholder={disabled ? undefined : placeholder}
          onChange={onChange ? (e) => onChange(e.target.value) : undefined}
          readOnly={readOnly}
          className={cx(
            textareaCss,
            css`
              resize: ${resize ? resize : 'none'};
              ${expand ? `height: 100%; width: 100%;` : undefined};
              ${errorMessage
                ? `border: solid 1px ${theme.color.red50};`
                : undefined};
            `,
            classNameTextArea ? classNameTextArea : undefined,
            readOnly || disabled ? textareaReadonlyCss : undefined
          )}
          value={value}
        />
      )}
    </FormTextContainer>
  )
}
